/**
 * useRights — ЕДИНСТВЕННЫЙ способ веба спросить об изменяемом праве.
 * Блок 2 · Ф8 «Права доступа» · В-О2.
 *
 * Права приходят полем `rights` профиля (`GET /auth/me`; ответ логина засевает
 * тем же полем) — отдельного запроса нет: он воспроизвёл бы «застревание»
 * домашней страницы до прихода данных (решение плана фазы П14).
 *
 * ─── Договор ────────────────────────────────────────────────────────────────
 *
 * 1. «НЕ ЗАГРУЖЕНО — ЗАКРЫТО». Пока профиль не пришёл, пришёл неполным, без
 *    поля `rights` либо с полем, которое не разобралось схемой договора
 *    (`parseRights` ниже), каждое право разбирается веткой `denied`. Экран при
 *    этом ведёт себя как «права нет» (Р-200, «на месте закрытого — ничего»),
 *    а не как «по должности можно». ⚠️ Это НЕ состояние ожидания: гейты
 *    маршрутов и домашняя страница на права не смотрят и не «застревают».
 *
 * 2. СОСТОЯНИЕ НЕ СРАВНИВАЕТСЯ — ОНО РАЗБИРАЕТСЯ. Хук отдаёт `match`, а не
 *    строку состояния: три ветки обязательны, ветка `granted` получает область
 *    права. Причины — шапка `packages/shared/src/rights.ts`:
 *      · `role` — НЕ «да» и НЕ «нет»: это выражение, стоявшее на этом месте до
 *        Ф8 (у администратора оно отвечает «да», у руководителя — «нет» либо
 *        «в своём отделе»);
 *      · `granted` у объектного права — «да» ТОЛЬКО там, где человек вошёл в
 *        объект (Р-204): `scope === 'OBJECT'` требует предиката места.
 *
 * 3. ЭТО ДЛЯ ИНТЕРФЕЙСА. Рубеж — сервер: он читает права из базы на каждом
 *    запросе. Вкладка, открытая до отзыва, получит штатный отказ на первом же
 *    действии (Р-217 г).
 *
 * Пример — «показывать ли вкладку расходов» (волна В-Д2):
 *
 *     const rights = useRights();
 *     const canViewExpenses = rights.match(UserRight.EXPENSE_READ, {
 *       denied: () => false,
 *       granted: (scope) => scope === RightGrantScope.ORG || isAssignedRo,
 *       role: () => прежнееВыражение,
 *     });
 */
import * as React from 'react';
import {
  ActorRightsSchema,
  USER_RIGHTS,
  matchRightState,
  resolveRightState,
  type ActorRights,
  type RightStateBranches,
  type UserRight,
} from '@aris/shared';

import { useMe, type MeUser } from './use-me';

/** Права текущего человека в форме, пригодной экрану. */
export interface RightsView {
  /**
   * `false` — права не загружены, и всё закрыто. Нужен ТОЛЬКО там, где экран
   * обязан отличить «закрыто» от «ещё не знаю» (скелет вместо пустоты);
   * решать по нему «можно ли» нельзя — для этого `match`.
   */
  readonly loaded: boolean;
  /** Разобрать право. Пока не загружено — всегда ветка `denied`. */
  match<T>(right: UserRight, branches: RightStateBranches<T>): T;
}

/** Ничего не загружено: каждое право — `denied`. */
export const RIGHTS_NOT_LOADED: RightsView = Object.freeze({
  loaded: false,
  match: <T>(_right: UserRight, branches: RightStateBranches<T>): T => branches.denied(),
});

/** Итог разбора одного значения `rights`; `null` — не разобралось. */
const parsedRights = new WeakMap<object, ActorRights | null>();

/**
 * Разобрать поле `rights` профиля СХЕМОЙ ДОГОВОРА (`ActorRightsSchema`) —
 * строго: неизвестное право, неизвестный эффект, сброшенная строка
 * (`INHERIT`), лишний ключ, не тот тип дают `null`, то есть «не загружено —
 * закрыто» для ВСЕХ прав (фикс-пак 1, п. 4).
 *
 * ⚠️ ПОЧЕМУ СТРОГО, А НЕ «ЧТО СМОГЛИ — ПРОЧЛИ». Ступень 1 читает поправку как
 * `own[right] === 'DENY'`. Значение, которого она не знает, для неё — «строки
 * нет», и администратор получил бы ветку должности, то есть «да», ровно там,
 * где сервер прислал нечто про запрет. Неразборчивое обязано закрывать.
 *
 * ⚠️ ЦЕНА, названная прямо: сервер НОВЕЕ веба (появилось девятое право, а
 * вкладка открыта со старой сборкой) — у человека с поправкой по новому праву
 * веб закроет всё до обновления страницы. Рубежом веб не является, так что это
 * неудобство, а не дыра; обратная ошибка была бы дырой.
 *
 * Итог запоминается по самому объекту: профиль в кэше запросов неизменяем,
 * а читателей у него много.
 */
function parseRights(value: unknown): ActorRights | null {
  if (typeof value !== 'object' || value === null) return null;
  const known = parsedRights.get(value);
  if (known !== undefined) return known;
  const result = ActorRightsSchema.safeParse(value);
  const rights = result.success ? result.data : null;
  parsedRights.set(value, rights);
  return rights;
}

/** Та часть профиля, из которой собираются права: уровень и поправки. */
export type RightsSource = Pick<MeUser, 'accessLevel' | 'rights'>;

/** Чистая сборка представления прав из профиля — для хука и для тестов. */
export function rightsViewOf(me: RightsSource | null | undefined): RightsView {
  if (me == null || me.accessLevel == null) return RIGHTS_NOT_LOADED;
  const rights = parseRights(me.rights);
  if (rights === null) return RIGHTS_NOT_LOADED;
  const { accessLevel } = me;
  return {
    loaded: true,
    match: (right, branches) =>
      matchRightState(resolveRightState(rights, accessLevel, right), right, branches),
  };
}

/** Права текущего человека. Перезапрос при переходе — `useSessionFreshness`. */
export function useRights(): RightsView {
  const { data: me } = useMe();
  return React.useMemo(() => rightsViewOf(me), [me]);
}

/**
 * Отпечаток состава прав — чтобы заметить, что он сменился. `null` — права не
 * загружены. Порядок ключей фиксирован: сравниваются права, а не то, как сервер
 * отсортировал поля.
 */
export function rightsFingerprint(me: RightsSource | null | undefined): string | null {
  if (me == null || me.accessLevel == null) return null;
  const rights = parseRights(me.rights);
  if (rights === null) return null;
  // Единственное место веба, читающее состав поправок напрямую: это
  // сравнение «изменилось ли», а не вопрос «можно ли» (тот — только `match`).
  return JSON.stringify([
    me.accessLevel,
    USER_RIGHTS.map((right) => rights.own[right] ?? null),
    USER_RIGHTS.map((right) => rights.deniedByGip.includes(right)),
  ]);
}
